import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { AuthState, User, LoginCredentials, UserRole } from '../types';
import { authApi } from '../api/authApi';
import { storage } from '../utils/helpers';

// Helper to get primary role from roles array
export function getPrimaryRole(roles: string[] | undefined): UserRole {
  if (!roles || !Array.isArray(roles) || roles.length === 0) {
    return UserRole.EMPLOYEE;
  }
  
  // Role hierarchy - higher priority first
  // IT_ADMIN is separate from standard hierarchy but higher than EMPLOYEE
  const roleHierarchy = [UserRole.SUPER_ADMIN, UserRole.PMO, UserRole.MANAGER, UserRole.IT_ADMIN, UserRole.EMPLOYEE];
  for (const role of roleHierarchy) {
    if (roles.includes(role)) {
      return role;
    }
  }
  return UserRole.EMPLOYEE;
}

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_USER'; payload: User };

// Initial state
const initialState: AuthState = {
  user: null,
  token: storage.getToken(),
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
}

// Context type
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshPermissions: () => Promise<void>;
  userRole: UserRole | null;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = storage.getToken();
      const userJson = storage.getUser();

      if (token && userJson) {
        try {
          const user = JSON.parse(userJson);
          dispatch({ type: 'AUTH_SUCCESS', payload: { user, token } });
        } catch (error) {
          storage.clear();
          dispatch({ type: 'AUTH_FAILURE', payload: 'Session expired' });
        }
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: '' });
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    dispatch({ type: 'AUTH_START' });

    try {
      const response = await authApi.login(credentials);
      
      // Store in localStorage - backend returns access_token
      storage.setToken(response.access_token);
      storage.setUser(JSON.stringify(response.user));

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.user, token: response.access_token },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: message });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      storage.clear();
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const refreshPermissions = useCallback(async () => {
    try {
      const response = await authApi.getCurrentUser();
      // Update local storage
      storage.setUser(JSON.stringify(response.user));
      // Update state
      dispatch({ type: 'UPDATE_USER', payload: response.user });
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
      // If token is invalid, logout
      if (error instanceof Error && error.message.includes('401')) {
        storage.clear();
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    }
  }, []);

  // Get primary role from roles array
  const userRole = state.user?.roles ? getPrimaryRole(state.user.roles) : null;

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    refreshPermissions,
    userRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook for using auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
